import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button/Button';
import type { MergedEvaluations, EvaluationAdjustment } from '../../types/merge';
import type { ReviewData, CriterionData } from '../../types/evaluation';

interface EvaluationPosition {
  targetId: string;
  aspectId: string;
  position: number;
  source: 'project1' | 'project2' | 'manual';
  confidence: number;
}

interface EvaluationsMergeProps {
  projectIds: [string, string];
  mergedTargets: ReviewData[];
  mergedAspects: CriterionData[];
  onComplete: (data: MergedEvaluations) => void;
  onBack: () => void;
  initialData?: MergedEvaluations;
}

export const EvaluationsMerge: React.FC<EvaluationsMergeProps> = ({
  projectIds,
  mergedTargets,
  mergedAspects,
  onComplete,
  onBack,
  initialData
}) => {
  const [evaluationPositions, setEvaluationPositions] = useState<EvaluationPosition[]>([]);
  const [adjustments, setAdjustments] = useState<EvaluationAdjustment[]>(initialData?.adjustments || []);
  const [userEdited, setUserEdited] = useState(initialData?.userEdited || false);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  // 初期化
  useEffect(() => {
    if (initialData?.merged && initialData.merged.length > 0) {
      setEvaluationPositions(initialData.merged);
    } else {
      generateInitialEvaluations();
    }
  }, [mergedTargets, mergedAspects]);

  const generateInitialEvaluations = () => {
    const positions: EvaluationPosition[] = [];
    
    mergedTargets.forEach((target, targetIndex) => {
      mergedAspects.forEach((aspect, aspectIndex) => {
        // 相対位置ベースの初期配置（中央付近）
        const basePosition = 50 + (Math.random() - 0.5) * 20; // 40-60の範囲
        
        positions.push({
          targetId: target.reviewTitle || target.title || '',
          aspectId: aspect.title,
          position: Math.max(0, Math.min(100, basePosition)),
          source: 'manual',
          confidence: 0.5
        });
      });
    });
    
    setEvaluationPositions(positions);
  };

  const updateEvaluationPosition = (targetId: string, aspectId: string, newPosition: number) => {
    setEvaluationPositions(prev => 
      prev.map(evaluation => 
        evaluation.targetId === targetId && evaluation.aspectId === aspectId
          ? { ...evaluation, position: newPosition, source: 'manual' as const }
          : evaluation
      )
    );
    
    // 調整記録を追加
    const originalPosition = evaluationPositions.find(
      evaluation => evaluation.targetId === targetId && evaluation.aspectId === aspectId
    )?.position || 50;
    
    if (originalPosition !== newPosition) {
      const adjustment: EvaluationAdjustment = {
        targetId,
        aspectId,
        originalPosition,
        newPosition,
        source: 'manual'
      };
      
      setAdjustments(prev => [
        ...prev.filter(adj => !(adj.targetId === targetId && adj.aspectId === aspectId)),
        adjustment
      ]);
    }
    
    setUserEdited(true);
  };

  const getEvaluationPosition = (targetId: string, aspectId: string): number => {
    const evaluation = evaluationPositions.find(
      evaluation => evaluation.targetId === targetId && evaluation.aspectId === aspectId
    );
    return evaluation?.position || 50;
  };

  const handleDragStart = (e: React.DragEvent, targetId: string, aspectId: string) => {
    setDraggedItem(`${targetId}:${aspectId}`);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropTargetId: string, dropAspectId: string) => {
    e.preventDefault();
    
    if (!draggedItem) return;
    
    const [dragTargetId, dragAspectId] = draggedItem.split(':');
    
    if (dragTargetId !== dropTargetId || dragAspectId !== dropAspectId) {
      // 位置を交換
      const dragPosition = getEvaluationPosition(dragTargetId, dragAspectId);
      const dropPosition = getEvaluationPosition(dropTargetId, dropAspectId);
      
      updateEvaluationPosition(dragTargetId, dragAspectId, dropPosition);
      updateEvaluationPosition(dropTargetId, dropAspectId, dragPosition);
    }
    
    setDraggedItem(null);
  };

  const autoArrangeByTarget = (targetId: string) => {
    const targetEvaluations = evaluationPositions.filter(evaluation => evaluation.targetId === targetId);
    const sortedEvaluations = [...targetEvaluations].sort((a, b) => a.position - b.position);
    
    sortedEvaluations.forEach((evaluation, index) => {
      const newPosition = (index / (sortedEvaluations.length - 1)) * 100;
      updateEvaluationPosition(evaluation.targetId, evaluation.aspectId, newPosition);
    });
  };

  const resetToDefaults = () => {
    generateInitialEvaluations();
    setAdjustments([]);
    setUserEdited(true);
  };

  const handleComplete = () => {
    const finalData: MergedEvaluations = {
      originalProjects: {},
      merged: evaluationPositions,
      adjustments,
      userEdited
    };
    
    onComplete(finalData);
  };

  return (
    <div className="evaluations-merge">
      <div className="merge-header">
        <h2>評価の統合</h2>
        <p>評価対象と観点の組み合わせごとに相対的な評価位置を調整してください</p>
        <div className="instructions">
          <h4>💡 操作方法</h4>
          <ul>
            <li>スライダーで各評価対象の相対的な位置を調整できます</li>
            <li>ドラッグ&ドロップで評価位置を交換できます</li>
            <li>「自動整列」で評価対象ごとに均等配置されます</li>
            <li>位置は0-100の相対値で表現されます</li>
          </ul>
        </div>
      </div>

      <div className="evaluation-controls">
        <Button variant="ghost" onClick={resetToDefaults}>
          初期配置にリセット
        </Button>
      </div>

      <div className="evaluation-matrix">
        <div className="matrix-header">
          <div className="matrix-cell matrix-cell--corner">評価対象 / 観点</div>
          {mergedAspects.map((aspect, index) => (
            <div key={index} className="matrix-cell matrix-cell--aspect-header">
              <div className="aspect-title">{aspect.title}</div>
              <div className="aspect-description">
                {(aspect.description || []).slice(0, 2).join('、')}
                {(aspect.description || []).length > 2 && '...'}
              </div>
            </div>
          ))}
        </div>

        {mergedTargets.map((target, targetIndex) => (
          <div key={targetIndex} className="matrix-row">
            <div className="matrix-cell matrix-cell--target-header">
              <div className="target-title">{target.reviewTitle || target.title || ''}</div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => autoArrangeByTarget(target.reviewTitle || target.title || '')}
                className="auto-arrange-btn"
              >
                自動整列
              </Button>
            </div>
            
            {mergedAspects.map((aspect, aspectIndex) => {
              const position = getEvaluationPosition(target.reviewTitle || target.title || '', aspect.title);
              const itemId = `${target.reviewTitle || target.title || ''}:${aspect.title}`;
              
              return (
                <div 
                  key={aspectIndex} 
                  className={`matrix-cell matrix-cell--evaluation ${draggedItem === itemId ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, target.reviewTitle || target.title || '', aspect.title)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, target.reviewTitle || target.title || '', aspect.title)}
                >
                  <div className="position-display">
                    <span className="position-value">{position.toFixed(0)}</span>
                  </div>
                  <div className="position-slider">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={position}
                      onChange={(e) => updateEvaluationPosition(
                        target.reviewTitle || target.title || '', 
                        aspect.title, 
                        parseInt(e.target.value)
                      )}
                      className="slider"
                    />
                  </div>
                  <div className="position-visual">
                    <div 
                      className="position-indicator"
                      style={{ left: `${position}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="merge-actions">
        <Button variant="ghost" onClick={onBack}>
          戻る
        </Button>
        <Button variant="primary" onClick={handleComplete}>
          次のステップへ
        </Button>
      </div>

      <style>{`
        .evaluations-merge {
          padding: 1rem;
        }

        .merge-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .merge-header h2 {
          font-size: 1.5rem;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .merge-header p {
          color: #6b7280;
          margin: 0 0 1rem 0;
        }

        .instructions {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 0.5rem;
          padding: 1rem;
          margin-top: 1rem;
        }

        .instructions h4 {
          color: #0c4a6e;
          margin: 0 0 0.5rem 0;
          font-size: 1rem;
        }

        .instructions ul {
          color: #075985;
          margin: 0;
          padding-left: 1.5rem;
        }

        .instructions li {
          margin-bottom: 0.25rem;
        }

        .evaluation-controls {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 1rem;
        }

        .evaluation-matrix {
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          overflow: hidden;
          background: white;
          margin-bottom: 2rem;
        }

        .matrix-header, .matrix-row {
          display: grid;
          grid-template-columns: 200px repeat(auto-fit, minmax(250px, 1fr));
        }

        .matrix-cell {
          padding: 1rem;
          border-right: 1px solid #e5e7eb;
          border-bottom: 1px solid #e5e7eb;
        }

        .matrix-cell:last-child {
          border-right: none;
        }

        .matrix-row:last-child .matrix-cell {
          border-bottom: none;
        }

        .matrix-cell--corner {
          background: #f9fafb;
          font-weight: 600;
          text-align: center;
        }

        .matrix-cell--aspect-header {
          background: #f3f4f6;
          text-align: center;
        }

        .matrix-cell--target-header {
          background: #fefefe;
          border-right: 2px solid #d1d5db;
        }

        .aspect-title {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .aspect-description {
          font-size: 0.75rem;
          color: #6b7280;
          line-height: 1.3;
        }

        .target-title {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .auto-arrange-btn {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          color: #3b82f6;
        }

        .matrix-cell--evaluation {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          cursor: grab;
          transition: all 0.2s;
        }

        .matrix-cell--evaluation:hover {
          background: #f9fafb;
        }

        .matrix-cell--evaluation.dragging {
          opacity: 0.5;
          cursor: grabbing;
        }

        .position-display {
          text-align: center;
        }

        .position-value {
          display: inline-block;
          background: #3b82f6;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          font-weight: 600;
          min-width: 3rem;
        }

        .position-slider {
          width: 100%;
        }

        .slider {
          width: 100%;
          height: 0.5rem;
          border-radius: 0.25rem;
          background: #e5e7eb;
          outline: none;
          -webkit-appearance: none;
          appearance: none;
        }

        .slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 1rem;
          height: 1rem;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
        }

        .slider::-moz-range-thumb {
          width: 1rem;
          height: 1rem;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: none;
        }

        .position-visual {
          position: relative;
          height: 0.25rem;
          background: #e5e7eb;
          border-radius: 0.125rem;
        }

        .position-indicator {
          position: absolute;
          top: -0.125rem;
          width: 0.5rem;
          height: 0.5rem;
          background: #10b981;
          border-radius: 50%;
          transform: translateX(-50%);
        }

        .merge-actions {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          margin-top: 2rem;
        }

        @media (max-width: 768px) {
          .merge-actions {
            flex-direction: column-reverse;
          }

          .matrix-header, .matrix-row {
            grid-template-columns: 150px repeat(auto-fit, minmax(200px, 1fr));
          }

          .matrix-cell {
            padding: 0.5rem;
          }

          .evaluation-matrix {
            overflow-x: auto;
          }

          .evaluation-matrix > div {
            min-width: 600px;
          }
        }
      `}</style>
    </div>
  );
};